import { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, Check, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useLanguage } from '../../context/LanguageContext';
import type { Chapter } from '../../types/wizard';

interface ChapterSelectProps {
  chapters: Chapter[];
  selected: Chapter[];
  onToggle: (chapter: Chapter) => void;
}

function getSelectTriggerCls(selectedCount: number): string {
  if (selectedCount > 0)
    return 'border-violet-500/50 bg-violet-500/10 text-violet-300 hover:border-violet-400';
  return 'border-slate-700 bg-slate-700/50 text-slate-400 hover:border-violet-500/40 hover:bg-violet-500/10';
}

export function ChapterSelect({ chapters, selected, onToggle }: Readonly<ChapterSelectProps>) {
  const { language, t } = useLanguage();
  const [open, setOpen] = useState(false);

  /** Chapter data carries both scripts, so the list follows the interface language. */
  const chapterName = (chapter: Chapter) =>
    language === 'bn' ? chapter.banglaName : chapter.name;

  function getTriggerLabel(count: number): string {
    if (count === 0) return t('wizard.chapterPlaceholder');
    if (count === 1) return t('wizard.chapterSelectedOne');
    return t('wizard.chapterSelected', { count });
  }
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered = chapters.filter(
    (ch) => ch.name.toLowerCase().includes(search.toLowerCase()) || ch.banglaName.includes(search)
  );

  const isSelected = (ch: Chapter) => selected.some((s) => s.id === ch.id);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="space-y-3">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex w-full items-center justify-between rounded-xl border-2 px-4 py-3 text-left text-sm font-medium transition-all duration-200',
          open
            ? 'border-violet-500 ring-2 ring-violet-500/20 bg-slate-800'
            : getSelectTriggerCls(selected.length)
        )}
      >
        <span>
          {getTriggerLabel(selected.length)}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-slate-400 transition-transform duration-200',
            open && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="relative z-20 animate-scale-in">
          <div className="absolute w-full rounded-2xl border border-slate-700 bg-slate-800 shadow-xl overflow-hidden">
            <div className="p-2.5 border-b border-slate-700">
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-700 border border-slate-600">
                <Search className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                <input
                  autoFocus
                  type="text"
                  placeholder={t('wizard.chapterSearch')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-500 text-white"
                />
              </div>
            </div>
            <ul className="max-h-56 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <li className="px-4 py-4 text-sm text-slate-400 text-center">
                  {t('wizard.chapterNone')}
                </li>
              ) : (
                filtered.map((ch) => {
                  const checked = isSelected(ch);
                  return (
                    <li key={ch.id}>
                      <button
                        type="button"
                        onClick={() => onToggle(ch)}
                        className={cn(
                          'flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors',
                          checked ? 'bg-violet-500/10' : 'hover:bg-slate-700'
                        )}
                      >
                        <span
                          className={cn(
                            'flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border-2 transition-all duration-150',
                            checked
                              ? 'bg-gradient-to-br from-violet-500 to-indigo-600 border-transparent'
                              : 'border-slate-600 bg-slate-700'
                          )}
                        >
                          {checked && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                        </span>
                        <span className="flex flex-col items-start">
                          <span
                            className={cn(
                              'font-medium',
                              checked ? 'text-violet-300' : 'text-white'
                            )}
                          >
                            {chapterName(ch)}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
            {selected.length > 0 && (
              <div className="px-4 py-2.5 border-t border-slate-700 bg-slate-800/80 flex justify-between items-center">
                <span className="text-xs text-slate-500">
                  {t('wizard.chapterSelectedCount', { count: selected.length })}
                </span>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="text-xs font-semibold text-violet-400 hover:text-violet-300"
                >
                  {t('common.done')} ✓
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Selected chips */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map((ch) => (
            <span
              key={ch.id}
              className="inline-flex items-center gap-1.5 rounded-full bg-violet-500/10 px-3 py-1 text-xs font-semibold text-violet-300 border border-violet-500/30"
            >
              {chapterName(ch)}
              <button
                type="button"
                onClick={() => onToggle(ch)}
                className="flex h-4 w-4 items-center justify-center rounded-full hover:bg-brand-200 transition-colors"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
